import React, { useEffect, useRef, useState } from 'react';
import { HorizonInterval } from 'src/models/Horizon';
import ReportHorizonSequencing from 'src/models/ReportHorizonSequencing';
import ReportHorizonSequencingXRef from 'src/models/ReportHorizonSequencingXRef';
import useFetch from 'use-http';
import HorizonSequencingComponent from '../TractComponents/AddHorizonComponents/HorizonSequencingComponent';
import RHFInputBox from 'src/components/controls/tempRHFControls/RHFInputBox';
import Button from 'src/components/controls/forms/redesign/FormButton';
import CollapsibleSection from 'src/components/common/CollapsibleSection';
// import Card from 'src/components/common/Card.redesign';

interface IReportPreviewDataProps {
    selectedHorizons: HorizonInterval[];
    selectedSequence: ReportHorizonSequencing;
    clientId: number;
    projectId: number;
    updateHorizonSequences: () => void;
}

const ReportPreviewData = (props: IReportPreviewDataProps) => {
    const sequenceWindowRef: React.MutableRefObject<HTMLDivElement> = useRef();

    const { put, post, response } = useFetch();
    const [horizonOrder, setHorizonOrder] = useState<HorizonInterval[]>(props.selectedHorizons);
    const [loadingDone, setLoadingDone] = useState<boolean>(false);
    const [moveToPosition, setMoveToPosition] = useState<number>(undefined);
    const [intervalList, setIntervalList] = useState<HorizonInterval[][]>([]);
    const [unsequencedList, setUnsequencedList] = useState<HorizonInterval[]>([]);

    useEffect(() => {
        setLoadingDone(false);

        let refHorizons = [...props.selectedHorizons];

        props.selectedSequence?.horizonSequences?.forEach(hs => {
            let index = refHorizons.indexOf(refHorizons.find(sh => sh.id === hs.horizonIntervalId));
            if (index > -1) {
                let reference = refHorizons[index];
                reference.sequencePosition = hs.sequencePosition;
                reference.from?.calcType();
                reference.to?.calcType();
                refHorizons[index] = reference;
            }
        });

        refHorizons = refHorizons.sort((r1, r2) => { return r1.sequencePosition - r2.sequencePosition; });
        setHorizonOrder(refHorizons);
    }, [props.selectedHorizons, props.selectedSequence]);

    useEffect(() => {
        setLoadingDone(horizonOrder?.length > 0);
        updateIntervalList();
    }, [horizonOrder]);

    const sequenceHorizons = async (sequence: Array<{ id: number; sequence: number }>) => {
        let sequenceReport: ReportHorizonSequencing = new ReportHorizonSequencing;
        let sequences: ReportHorizonSequencingXRef[] = [];
        sequenceReport.projectId = props.projectId;
        sequenceReport.clientId = props.clientId;

        if (props.selectedSequence !== undefined) {
            sequenceReport = props.selectedSequence;
            sequences = sequenceReport.horizonSequences;

            sequence.forEach(seq => {
                let index = sequences.indexOf(sequences.find(s => s.horizonIntervalId === seq.id));
                if (index > -1) {
                    let ref = sequences[index];
                    ref.sequencePosition = seq.sequence;
                    sequences[index] = ref;
                } else {
                    sequences.push(new ReportHorizonSequencingXRef(seq.id, seq.sequence));
                }
            });

            await put(`v1.0/reports/sequence-horizons`, sequenceReport);
        } else {
            sequence.forEach(s => {
                sequences.push(new ReportHorizonSequencingXRef(s.id, s.sequence));
            });
            sequenceReport.horizonSequences = sequences;
    
            await post(`v1.0/reports/sequence-horizons`, sequenceReport);
        }

        if (response.ok) {
            props.updateHorizonSequences();
        }
    };

    // Sequence Numbers are pushed up by 1, to prevent confusion for the user on a 0-index.
    const horizonCard = (interval: HorizonInterval, currSequence: number, sequenceLength: number, currIndex = -1) => {
        if (!interval?.to) {
            return <span>Horizon depth undefined</span>;
        }

        return (
            <div style={{ width: '100%', display: 'flex', justifyContent: 'flex-start', alignItems: 'center' }}>
                <div style={{ width: '40%' }}>{`To ${interval.to.toString()}`}</div>
                <RHFInputBox
                    label="Move To Position: "
                    name=""
                    type="number"
                    style={{ width: '100%' }}
                    defaultVal={(currSequence + 1).toString()}
                    max={sequenceLength}
                    min={1}
                    bodyStyle={{ width: '10%', marginLeft: '.5rem' }}
                    onChange={(e) => updateSequence(e.currentTarget.value)}
                />
                <Button 
                    className="blue-hover-button" 
                    style={{ width: '20rem', height: '3rem', marginLeft: '.5rem' }} 
                    text={'Move'}
                    disabled={moveToPosition === undefined}
                    onClick={() => moveIntervals(currSequence, currIndex)}
                />
                {currIndex > -1 && (
                    <Button 
                        className="blue-hover-button" 
                        style={{ width: '20rem', height: '3rem', marginLeft: '.5rem' }} 
                        text={'Remove'}
                        onClick={() => moveIntervals(currSequence, currIndex, true)}
                    />
                )}
            </div>
        );
    };    

    // Sequence Numbers are pushed up by 1, to prevent confusion for the user on a 0-index.
    const customHorizonSequenceFormatting = (intervals: HorizonInterval[], currSequence: number, sequenceLength: number) => {
        // Return Grouped Card Logic for Sequenced Horizons.
        let iFirst = intervals[0];
        
        if (!iFirst?.to) {
            return <span>Horizon depth undefined</span>;
        }

        return (
            <div className="mb-1 mt-1" style={{ display: 'flex', flexDirection: 'column', width: '100%' }}>
                {horizonCard(iFirst, currSequence, sequenceLength, 0)}
                {intervals.length > 1 && (
                    <div className="ml-3 mt-2 mb-2" style={{ display: 'flex', flexDirection: 'column' }} key={`sequence-position-${currSequence}`}>
                        <CollapsibleSection title="Same As Horizons">
                            {intervals.slice(1).map((hi: HorizonInterval, index: number) => {
                                // Remember that index should be 1 higher, due to omitting the first.
                                return horizonCard(hi, currSequence, sequenceLength, index + 1);
                            })}
                        </CollapsibleSection>
                    </div>
                )}
            </div>
        );
    };

    const updateSequence = (item: string) => {
        setMoveToPosition(parseInt(item, 10) - 1);
    };

    const moveIntervals = (currSequence: number, currIndex: number = -1, remove: boolean = false) => {
        let dest = remove ? -1 : moveToPosition;
        reorderList(currSequence, dest, true, currIndex);
    };

    const reorderList = (sourcePosition: number, destPosition: number, combine: boolean, sourceIndex: number = -1) => {
        let result = [...intervalList];
        let unsequenced = [...unsequencedList];
        // Account for spliced array (Ignore if grabbing single).
        if (sourcePosition < destPosition && sourcePosition > -1 && sourceIndex === -1) {
            destPosition -= 1;
        }

        // if sequenced and Indexed, grab single
        // else if sequenced, grab sourcePosition
        // else grab unsequenced
        const [removed] = sourcePosition > -1 ? sourceIndex > -1 ? [result[sourcePosition].splice(sourceIndex, 1)] : result.splice(sourcePosition, 1) : [unsequenced.splice(sourceIndex, 1)];
        // if sequencing, splice into array
        // Else, concat with unsequenced list.
        if (destPosition > -1 && combine) {
            result[destPosition] = result[destPosition].concat(removed);
        } else if (destPosition > -1) {
            result.splice(destPosition, 0, removed);
        } else {
            unsequenced = unsequenced.concat(removed);
        }

        result = result.map((hArr: Array<HorizonInterval>, hArrIndex: number) => {
            return hArr.map((hi: HorizonInterval) => {
                hi.sequencePosition = hArrIndex + 1;
                return hi;
            });
        });
        unsequenced = unsequenced.map((hi: HorizonInterval) => {
            hi.sequencePosition = -1;
            return hi;
        });

        setIntervalList(result);
        setUnsequencedList(unsequenced);
    };

    const updateIntervalList = () => {
        // logic to set Interval List.
        let horizons = [...horizonOrder];

        let newInterval = [];
        if (props.selectedSequence !== undefined) {
            horizons = horizons.sort((a, b) => {
                if (a.sequencePosition < b.sequencePosition) {
                    return -1;
                }
                if (a.sequencePosition > b.sequencePosition) {
                    return 1;
                }
                return 0;
            });
    
            for (let h of horizons) {
                if (h.sequencePosition > newInterval.length) {
                    newInterval.push([h]);
                } else {
                    newInterval[h.sequencePosition - 1].push(h);
                }
            }
            setUnsequencedList([]);
        } else {
            let depthHorizons = horizons.filter(dh => !!dh.to?.depth);
            let customHorizons = horizons.filter(h => !h.to?.depth && h.to?.isCenterEarth !== true);
            let coeHorizons = horizons.filter(h => h.to?.isCenterEarth === true);
    
            depthHorizons = depthHorizons.sort((a, b) => {
                return a.to.depth - b.to.depth;
            });
        
            let currDepth = 0;
    
            for (let h of depthHorizons) {
                if (h.to.depth === currDepth) {
                    newInterval[newInterval.length - 1].push(h);
                } else {
                    newInterval.push([h]);
                    currDepth = h.to.depth;
                }
            }
    
            if (coeHorizons.length > 0) {
                newInterval.push(coeHorizons);
            }
            setUnsequencedList(customHorizons);
        }

        setIntervalList(newInterval);
    };

    return (
        <div ref={sequenceWindowRef} style={{ paddingBottom: '1rem' }}>
            {loadingDone && (
                <HorizonSequencingComponent
                    horizons={horizonOrder}
                    intervalList={intervalList}
                    unsequencedList={unsequencedList}
                    sequenceHorizons={sequenceHorizons}
                    isInDialog={false}
                    hasMultipleTracts={true}
                    viewportRef={sequenceWindowRef}
                    customListFormat={customHorizonSequenceFormatting}
                    customDetailFormat={horizonCard}
                    reorderList={reorderList}
                />
            )}
        </div>
    );
};

export default ReportPreviewData;



// WEBPACK FOOTER //
// ./src/components/project/sections/ReportComponents/ReportPreviewData.tsx